import { useState, useRef } from 'react';
import { CopyIcon, CheckIcon, AttentionTriangleIcon } from '@rsos/assets/icons';
import { Box, Button, Modal, Text } from '@rsos/base-components';
import { MODAL_BACKDROP_ZINDEX } from '@rsos/base-components/Modal/Modal.styles';
import {
  ModalFooter,
  ModalBody,
} from '@rsos/components/capstone/base/Modal/ModalComponents';
import Popover from '@rsos/components/capstone/base/Popover';
import {
  PINBox,
  ButtonContent,
  StyledPhoneNumber,
  PopoverBox,
  PopoverText,
} from './TextToVoiceModal.styles';

const TextToVoiceModal = ({
  isOpened,
  onClose,
  pinJobID,
  midasConferenceNumber,
  error = null,
  onRetry = () => {},
}) => {
  const PIN = (pinJobID || '').split('');
  const [isPinCopied, setIsPinCopied] = useState(false);
  const [isPhoneNumberCopied, setIsPhoneNumberCopied] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const popoverRef = useRef();

  const handleCopyPIN = () => {
    if (!navigator.clipboard) {
      // eslint-disable-next-line no-console -- browser capability not available
      console.error(
        'Copy feature unavailable. It is only available on a secure (HTTPS) connection.',
      );
      return;
    }
    navigator.clipboard.writeText(pinJobID || '');
    setIsPinCopied(true);
    setTimeout(() => {
      setIsPinCopied(false);
    }, 3000);
  };

  const handlePhoneNumberCopy = () => {
    if (!navigator.clipboard) {
      // eslint-disable-next-line no-console -- browser capability not available
      console.error(
        'Copy feature unavailable. It is only available on a secure (HTTPS) connection.',
      );
      return;
    }
    const massagedNumber = midasConferenceNumber.replace(/\D/g, '');
    navigator.clipboard.writeText(massagedNumber);
    setIsPhoneNumberCopied(true);
    setTimeout(() => {
      setIsPhoneNumberCopied(false);
    }, 3000);
  };

  const handleHover = isVisible => setIsTooltipVisible(isVisible);
  const handleMouseEnter = () => handleHover(true);
  const handleMouseLeave = () => handleHover(false);

  const hasSessionData = pinJobID && midasConferenceNumber;

  return (
    <Modal
      isOpened={isOpened}
      onClose={onClose}
      width={350}
      id="text-to-voice-modal"
      data-name="text-to-voice-modal"
    >
      <ModalBody onClose={onClose}>
        {error ? (
          // Error State
          <Box flow={20}>
            <Box horizontal align="center" gridColumnGap="8px" justify="center">
              <AttentionTriangleIcon
                width={20}
                height={20}
                color="warningDark"
              />
              <Text size="xlarge" color="primaryText">
                Translation Unavailable
              </Text>
            </Box>
            <Text size="small" color="secondaryText" textAlign="center">
              {error}
            </Text>
          </Box>
        ) : hasSessionData ? (
          // Success State
          <Box flow={20}>
            <Text size="xlarge" color="primaryText">
              Call{' '}
              <Popover
                cssDisplayProperty="inline-block"
                data-name="phone-number-tooltip"
                isOpen={isTooltipVisible}
                popoverID="phone-number-tooltip"
                trigger={
                  <StyledPhoneNumber
                    data-id="midas-conference-number"
                    onClick={handlePhoneNumberCopy}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    {midasConferenceNumber}
                  </StyledPhoneNumber>
                }
                useCenterXOffset
                yOffset={58}
                zIndex={MODAL_BACKDROP_ZINDEX}
              >
                <PopoverBox ref={popoverRef}>
                  <PopoverText>
                    {isPhoneNumberCopied ? 'Copied' : 'Copy'}
                  </PopoverText>
                </PopoverBox>
              </Popover>{' '}
              to use Text-to-Voice
            </Text>
            <Text size="small" color="secondaryText" textAlign="left">
              Call the number above and enter the 6 digit PIN number below to
              connect our HARMONY AI Translator for real-time translation.
            </Text>
          </Box>
        ) : null}

        {hasSessionData && (
          <Box
            horizontal
            mt={20}
            justify="center"
            align="center"
            gridColumnGap="8px"
          >
            {PIN.map((val, index) => (
              <PINBox key={index}>{val}</PINBox>
            ))}
          </Box>
        )}
      </ModalBody>
      <ModalFooter flow={12}>
        {error ? (
          // Error State Buttons
          <>
            <Button
              btnType="tertiary"
              onClick={onClose}
              grow
              noMargin
              data-name="close-button"
            >
              Close
            </Button>
            <Button
              data-name="retry-button"
              btnType="primary"
              onClick={onRetry}
              grow
              noMargin
            >
              Try Again
            </Button>
          </>
        ) : hasSessionData ? (
          // Success State Buttons
          <>
            <Button
              btnType="tertiary"
              onClick={onClose}
              grow
              noMargin
              data-name="close-button"
            >
              Close
            </Button>
            <Button
              data-name="text-voice-button"
              btnType="primary"
              onClick={handleCopyPIN}
              grow
              noMargin
            >
              <ButtonContent>
                {isPinCopied ? (
                  <CheckIcon height={12} width={12} />
                ) : (
                  <CopyIcon height={12} width={12} />
                )}
                <Text>{isPinCopied ? 'Copied' : 'Copy PIN'}</Text>
              </ButtonContent>
            </Button>
          </>
        ) : (
          // Fallback - just close button
          <Button
            btnType="tertiary"
            onClick={onClose}
            grow
            noMargin
            data-name="close-button"
          >
            Close
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default TextToVoiceModal;
